// ============================================================================
// GroupedList.tsx — ЕДИНЫЙ механизм ГРУППИРОВОК списков (любых форм списков:
// заявки, задачи, справочники, документы, регистры...). Разница — только поля.
//
// Режимы (mode):
//   sections — секции с липкими заголовками (сворачивание, счётчики);
//   kanban   — колонки-статусы (карточки/строки внутри);
//   chips    — чипы-итоги сверху (клик — фильтр по группе);
//   cards    — ГРУППЫ-КАРТОЧКИ: клик по группе ведёт к вложенной группе
//              (следующее поле группировки) или к самим объектам (drill-down).
//              Навигация «← Все группы» + кликабельные крошки видны ВСЕГДА
//              (в т.ч. после провала до объектов).
// Рендер элементов — renderItems (карточки/таблица) — передаёт вызывающий
// (AutoListForm), поэтому механизм один и без дублей.
//
// persistKey — уровень провала/чип/свёрнутость ПЕРЕЖИВАЮТ открытие формы
// объекта и возврат (состояние хранится в модульном кэше по ключу списка).
// ============================================================================

import { useState, type ReactNode } from 'react';
import { CardGrid, cardNarrowClass } from '@/components/ui/CardGrid';

interface GroupedListProps {
  mode: string;
  /** Поля группировки по порядку (вложенность для режима «карточки-группы»). */
  fields: string[];
  rows: Record<string, unknown>[];
  /** Значение группировки для строки (поддержка частей даты: «поле:year|month|day»). */
  valueOf?: (field: string, row: Record<string, unknown>) => string;
  /** Подпись значения группы (напр., «На рассмотрении»): value + образец строки. */
  labelFor: (field: string, value: unknown, sample?: Record<string, unknown>) => string;
  /** Рендер элементов (карточки/таблица) — переиспользуется во всех режимах. */
  renderItems: (rows: Record<string, unknown>[]) => ReactNode;
  /** Ключ списка для сохранения состояния провала (переживает открытие формы). */
  persistKey?: string;
}

interface Group {
  key: string;
  label: string;
  rows: Record<string, unknown>[];
}

interface DrillEntry {
  field: string;
  value: string;
  label: string;
}

interface ListUiState {
  drill: DrillEntry[];
  chip: string;
  collapsed: Record<string, boolean>;
}

// Модульный кэш состояния списка: ключ → {drill, chip, collapsed}.
// Переживает remount AutoListForm/GroupedList (открытие формы объекта и возврат).
const uiCache = new Map<string, ListUiState>();

function buildGroups(
  field: string,
  rows: Record<string, unknown>[],
  valueOf: NonNullable<GroupedListProps['valueOf']>,
  labelFor: GroupedListProps['labelFor'],
): Group[] {
  const map = new Map<string, Group>();
  for (const r of rows) {
    const key = valueOf(field, r);
    if (!map.has(key)) {
      map.set(key, { key, label: labelFor(field, key, r), rows: [] });
    }
    map.get(key)!.rows.push(r);
  }
  const groups = Array.from(map.values());
  // Детерминированный порядок групп (русская локаль). Пустое значение — в конце.
  groups.sort((a, b) => {
    if (a.key === '' && b.key !== '') return 1;
    if (b.key === '' && a.key !== '') return -1;
    return a.label.localeCompare(b.label, 'ru');
  });
  return groups;
}

function CountBadge({ n }: { n: number }) {
  return (
    <span className="ml-1 rounded-full bg-background border px-1.5 text-[10px] text-muted-foreground">{n}</span>
  );
}

// Валидация восстановленного провала: он должен соответствовать ТЕКУЩИМ полям
// группировки (иначе поля сменили — провал сбрасывается детерминированно).
function validateDrill(raw: DrillEntry[], groupFields: string[]): DrillEntry[] {
  const out: DrillEntry[] = [];
  for (let i = 0; i < raw.length && i < groupFields.length; i++) {
    if (raw[i]?.field !== groupFields[i]) break;
    out.push(raw[i]);
  }
  return out;
}

export function GroupedList({ mode, fields, rows, valueOf, labelFor, renderItems, persistKey }: GroupedListProps) {
  const groupFields = (fields || []).filter(Boolean);
  const valueOfFn: NonNullable<GroupedListProps['valueOf']> = valueOf
    || ((f, r) => {
      const v = r[f];
      return v === null || v === undefined ? '' : String(v);
    });
  const restored = persistKey ? uiCache.get(persistKey) : undefined;
  const [collapsed, setCollapsedState] = useState<Record<string, boolean>>(restored?.collapsed || {});
  const [chip, setChipState] = useState<string>(restored?.chip || '');
  const [drill, setDrillState] = useState<DrillEntry[]>(
    restored ? validateDrill(restored.drill || [], groupFields) : [],
  );

  const save = (d: DrillEntry[], c: string, col: Record<string, boolean>) => {
    if (persistKey) uiCache.set(persistKey, { drill: d, chip: c, collapsed: col });
  };
  const setDrill = (updater: DrillEntry[] | ((prev: DrillEntry[]) => DrillEntry[])) => {
    setDrillState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      save(next, chip, collapsed);
      return next;
    });
  };
  const setChip = (v: string) => {
    setChipState(v);
    save(drill, v, collapsed);
  };
  const setCollapsed = (updater: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => {
    setCollapsedState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      save(drill, chip, next);
      return next;
    });
  };

  // Группировать не по чему — отдаём как есть (обычный список).
  if (groupFields.length === 0) return <>{renderItems(rows)}</>;

  const primaryField = groupFields[0];

  if (mode === 'sections') {
    const groups = buildGroups(primaryField, rows, valueOfFn, labelFor);
    return (
      <div className="flex-1 min-h-0 overflow-auto flex flex-col gap-3">
        {groups.map((g) => (
          <div key={g.key} className="flex flex-col">
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
              className="sticky top-0 z-10 flex items-center gap-2 text-xs font-medium bg-muted/70 backdrop-blur px-2 py-1.5 rounded-md border border-border/60 text-left"
            >
              <span className="text-[10px]">{collapsed[g.key] ? '▶' : '▼'}</span>
              <span className="truncate">{g.label}</span>
              <CountBadge n={g.rows.length} />
            </button>
            {!collapsed[g.key] && <div className="mt-1 min-h-0">{renderItems(g.rows)}</div>}
          </div>
        ))}
      </div>
    );
  }

  if (mode === 'chips') {
    const groups = buildGroups(primaryField, rows, valueOfFn, labelFor);
    const filtered = chip === '' ? rows : (groups.find((g) => g.key === chip)?.rows || []);
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => setChip('')}
            className={`text-[11px] whitespace-nowrap rounded-full px-2.5 py-0.5 border transition-colors ${chip === '' ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
          >
            Все · {rows.length}
          </button>
          {groups.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setChip(g.key)}
              className={`text-[11px] whitespace-nowrap rounded-full px-2.5 py-0.5 border transition-colors ${chip === g.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
            >
              {g.label} · {g.rows.length}
            </button>
          ))}
        </div>
        <div className="flex-1 min-h-0">{renderItems(filtered)}</div>
      </div>
    );
  }

  if (mode === 'kanban') {
    const groups = buildGroups(primaryField, rows, valueOfFn, labelFor);
    return (
      <div className="flex-1 min-h-0 overflow-x-auto flex gap-2 items-stretch">
        {groups.map((g) => (
          <div key={g.key} className="flex flex-col w-[300px] shrink-0 min-h-0 rounded-lg border border-border/60 bg-muted/20">
            <div className="shrink-0 px-2 py-1.5 text-xs font-medium border-b border-border/60 flex items-center gap-1">
              <span className="truncate">{g.label}</span>
              <CountBadge n={g.rows.length} />
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-1">{renderItems(g.rows)}</div>
          </div>
        ))}
      </div>
    );
  }

  if (mode === 'cards') {
    // Провал (drill-down): уровень = число выбранных групп; клик по группе-карточке
    // ведёт либо к следующему полю группировки, либо к самим объектам (последний уровень).
    // ВАЖНО: навигация («← Все группы» + крошки) видна ВСЕГДА, в т.ч. на уровне объектов.
    let current = rows;
    for (const d of drill) current = current.filter((r) => valueOfFn(d.field, r) === d.value);
    const field = groupFields[drill.length];
    const groups = field ? buildGroups(field, current, valueOfFn, labelFor) : [];
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-2">
        {drill.length > 0 && (
          <div className="flex items-center gap-1.5 shrink-0 text-xs flex-wrap">
            <button
              type="button"
              className="rounded-md border px-2 py-0.5 hover:bg-muted font-medium"
              onClick={() => setDrill([])}
              title="Вернуться к списку групп"
            >
              ← Все группы
            </button>
            {drill.map((d, i) => (
              <span key={`${d.field}:${d.value}`} className="flex items-center gap-1.5">
                <span className="text-muted-foreground">/</span>
                <button
                  type="button"
                  className={`rounded-md border px-2 py-0.5 hover:bg-muted ${i === drill.length - 1 ? 'bg-muted' : ''}`}
                  onClick={() => setDrill((cur) => cur.slice(0, i + 1))}
                  title="Вернуться на этот уровень"
                >
                  {d.label}
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-auto">
          {field ? (
            <CardGrid>
              {(narrow) => groups.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setDrill((d) => [...d, { field, value: g.key, label: g.label }])}
                  className={`${cardNarrowClass(narrow)} text-left rounded-lg border border-border/60 bg-card p-3 hover:bg-accent/40 transition-colors`}
                >
                  <div className="text-sm font-medium truncate">{g.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{g.rows.length} зап.</div>
                </button>
              ))}
            </CardGrid>
          ) : (
            renderItems(current)
          )}
        </div>
      </div>
    );
  }

  // Неизвестный режим — как есть (без фоллбэков на «секции»).
  return <>{renderItems(rows)}</>;
}
